import EventTypes from 'components/EventTypes';
import createLogger from 'components/logger/Logger';
import storage from 'components/utils/storage/Storage';
import StorageKeys from 'components/utils/storage/StorageKeys';

const Logger = createLogger('WishlistHandler');

const endpoints = {
  WISH_LIST: `/${AppSettings.locale}/api/v1/wishlist`,
};

/**
 * Retrieves all wishlist CTAs from DOM based on product code
 * @param productCode
 * @returns {HTMLButtonElement[]}
 */
const getDomProducts = productCode =>
  [].slice.call(document.querySelectorAll(`.js-wishlist-cta[data-product-id="${productCode}"]`), 0);

/**
 * Enables wishlist CTAs
 */
const enableWishlistCtas = () => {
  const wishlistCtas = document.querySelectorAll('.js-wishlist-cta');

  if (!wishlistCtas.length) {
    return;
  }

  wishlistCtas.forEach(wishlistCta => wishlistCta.removeAttribute('disabled'));
};

/**
 * Handles wishlist CTA aria attributes
 * @param {HTMLButtonElement} product
 */
const handleWishlistAria = (productWishlistBtn, isActivating) => {
  const isPressed = productWishlistBtn.getAttribute('aria-pressed') === isActivating.toString();

  if (isPressed) {
    return;
  }

  const ariaLabel = productWishlistBtn.getAttribute('aria-label');
  const newAriaLabel = productWishlistBtn.dataset.msg;

  productWishlistBtn.setAttribute('aria-label', newAriaLabel);
  productWishlistBtn.dataset.msg = ariaLabel;
  productWishlistBtn.setAttribute('aria-pressed', isActivating);
};

/**
 * Marks as "wish-listed" all products on the page with specific product code
 * @param {string} productCode
 */
const activateWishlistIcon = productCode => {
  const domProducts = getDomProducts(productCode);

  domProducts.forEach(product => {
    product.classList.remove('icon-heart-empty');
    product.classList.add('icon-heart-full');
    product.dataset.actionType = EventTypes.WISHLIST_REMOVE_ITEM_REQUEST;

    handleWishlistAria(product, true);
  });
};

/**
 * Marks as "Non-wish-listed" all products on the page with specific product code
 * @param {string} productCode
 */
const deactivateWishlistIcon = productCode => {
  const domProducts = getDomProducts(productCode);

  domProducts.forEach(product => {
    product.classList.remove('icon-heart-full');

    if (product.parentNode.classList.contains('product-wishlist-wrapper')) {
      product.classList.add('icon-heart-empty-thin');
    } else {
      product.classList.add('icon-heart-empty');
    }

    product.dataset.actionType = EventTypes.WISHLIST_ADD_ITEM_REQUEST;

    handleWishlistAria(product, false);
  });
};

/**
 * Clears wishlist data from localStorage
 */
const clearWishlistCache = () => {
  storage.removeItem(StorageKeys.WISHLIST_ENTRIES);
};

/**
 * Gets wishlist products from local storage
 * @returns {Array}
 */
const getWishlistProductsFromLocalStorage = () => {
  const wishlistEntries = storage.getItem(StorageKeys.WISHLIST_ENTRIES)?.products;

  return wishlistEntries || [];
};

/**
 * Retrieves wishlist entries from endpoint
 * @returns {Array}
 */

const getWishlistEntries = async () => {
  const cache = getWishlistProductsFromLocalStorage();
  const wishlistItemCount = getWishlistItemsCountFromCookie();

  if (wishlistItemCount === cache.length) {
    return cache;
  }

  try {
    const response = await fetch(endpoints.WISH_LIST);
    const json = await response.json();

    if (response.ok && json) {
      const products = json.results?.map(parseProduct) || [];

      storage.setItem(StorageKeys.WISHLIST_ENTRIES, { isFetched: true, products });
      return products;
    }
  } catch (error) {
    if (error instanceof SyntaxError) {
      Logger.error(`Syntax error parsing JSON: ${error}`);
    } else {
      Logger.error(`Error fetching the wishlist entries: ${error}`);
    }
    return [];
  }
};

/**
 * Retrieves wishlist items quantity from cookie
 * @returns {Number}
 */
const getWishlistItemsCountFromCookie = () => {
  const cookieArray = decodeURIComponent(document.cookie).split(';');

  return cookieArray.reduce((acc, cookie) => {
    if (cookie.includes(StorageKeys.WISHLIST_COOKIE)) {
      return Number(cookie.split(':').pop()) || 0;
    }
    return acc;
  }, 0);
};

/*
 * Maps product data to expected image data
 * @param {Object} product
 */
const parseProduct = product => {
  const { simplifiedImageData, ...restProduct } = product;
  const simplifiedImages = Object.keys(simplifiedImageData || {}).map(type => ({
    value: simplifiedImageData[type],
    key: type,
  }));

  return {
    ...restProduct,
    simplifiedImages,
  };
};

/**
 * Remove product from wishlist cache by code
 * @param {string} code
 */
const removeProductFromWishlistCacheByCode = code => {
  const wishlist = storage.getItem(StorageKeys.WISHLIST_ENTRIES);

  if (!wishlist?.products?.length) {
    return;
  }

  const products = wishlist.products.filter(product => product.code !== code);

  storage.setItem(StorageKeys.WISHLIST_ENTRIES, JSON.stringify({ ...wishlist, products }));
};

export {
  activateWishlistIcon,
  deactivateWishlistIcon,
  clearWishlistCache,
  removeProductFromWishlistCacheByCode,
  getWishlistEntries,
  getWishlistItemsCountFromCookie,
  getWishlistProductsFromLocalStorage,
  enableWishlistCtas,
  parseProduct,
};
